import { useQuery } from '@apollo/client';
import InfoIcon from '@atlaskit/icon/glyph/info';
import Tooltip from '@atlaskit/tooltip';
import { generatedTypesNew } from '@css-xp/graphtypes';
import { InvoicePaymentMethod } from '@css-xp/graphtypes/src/generated-types-new';
import { NEW_DATE_FORMAT, NEW_DATE_TIME_FORMAT } from '@atlassian/cssxp-typescommon';
import { DEFAULT_PAGE, DEFAULT_ROWS_PER_PAGE } from '@atlassian/cssxp-utils-entitlement';
import { GloballyAllowedSidebar } from '@atlassian/cssxp-utils-generic';
import { getIdentifier } from '@atlassian/cssxp-utils-invoice';
import {
  ColGroup,
  commonTabs,
  CustomDynamicResultTable,
  FormattedAddress,
  FormattedDate,
  formattedV2,
  HeadCell,
  InvoiceUtil,
  OpenSidebarRow,
  SearchResultColumn,
  SimpleTD,
  TableBody,
  TableHead,
  useSidebar,
} from '@css-xp/base';
import { InvoiceChildRefundRow } from '@css-xp/billing-v2';
import classNames from 'classnames';
import { isEmpty } from 'lodash';
import isEqual from 'lodash/isEqual';
import memoize from 'lodash/memoize';
import React, { memo, useCallback, useState } from 'react';
import './contact-invoice-tab.scss';
import { fetchInvoicesForCloud } from './query/fetch-cloud-invoice';

type ColumnList = HeadCell & SearchResultColumn;

const getIsFreeInvoice = memoize((paymentMethod?: any | null) => {
  return paymentMethod === generatedTypesNew.InvoicePaymentMethod.NOPAYMENT;
});

const getPaymentMethod = (paymentId: string | undefined | null, creditCard: any) => {
  if (paymentId) {
    if (
      paymentId === generatedTypesNew.InvoicePaymentMethod.PAIDCC ||
      paymentId === generatedTypesNew.InvoicePaymentMethod.STRIPE_CARD
    ) {
      return creditCard?.last4Numbers ? `**** **** **** ${creditCard.last4Numbers}` : '';
    }
    if (paymentId === InvoicePaymentMethod.DEFERRED) {
      return `Payment on terms`;
    }
    if (
      paymentId === generatedTypesNew.InvoicePaymentMethod.PAIDONACCOUNT ||
      paymentId === generatedTypesNew.InvoicePaymentMethod.PAID_BANK
    ) {
      return 'Pay on Account';
    }
  }
  return 'Not available';
};

const getFormattedAddress = (address: any, organizationName?: string) => (
  <span className="line-clamp-sold-to-address">
    <FormattedAddress
      addressLine1={address?.address1}
      addressLine2={address?.address2}
      city={address?.city}
      country={address?.country}
      postcode={address?.postalCode}
      state={address?.state}
      organizationName={organizationName}
    />
  </span>
);

const getAddress = (address: any | undefined | null, organizationName?: string) => {
  if (!address) {
    return 'No address information';
  } else {
    return getFormattedAddress(address, organizationName);
  }
};

const getDueOnCell = (cellName: string) => {
  return (
    <th className="elem-with-tooltip">
      <Tooltip content={'Overdues in open invoices will be marked in red'} tag="span">
        <InfoIcon label="Info" size="small" />
      </Tooltip>
      <span className="ml-4">{cellName}</span>
    </th>
  );
};

const getHead = memoize(() => {
  return [
    {
      name: 'Invoice number',
      content: 'Invoice number',
    },
    {
      name: 'Sold-to address',
      content: 'Sold-to address',
    },
    {
      name: 'Billing address',
      content: 'Billing address',
    },
    {
      name: 'Due on',
      content: 'Due on',
      renderComponent: getDueOnCell('Due on'),
    },
    {
      name: 'Issued on',
      content: 'Issued on',
    },
    {
      name: 'Payment method',
      content: 'Payment method',
      isMandatory: false,
      isChecked: true,
    },
    {
      name: 'Amount',
      content: 'Amount',
      isMandatory: false,
      isChecked: true,
    },
  ];
});

const getColumnData = (visibleColumns: ColumnList[], invoice: generatedTypesNew.Invoice) => {
  const {
    id,
    invoiceNumber,
    status,
    dueDate,
    grandTotal,
    paymentCurrency,
    paymentMethod,
    creditCard,
    shipTo,
    billTo,
    pdfLink,
    transactionAccount,
    createdTimestamp,
    total,
  } = invoice;
  const rowData = visibleColumns.map((column) => {
    switch (column.name) {
      case 'Invoice number':
        return (
          <td>
            <commonTabs.InvoiceCell
              invoiceId={id}
              status={status}
              invoiceNumber={invoiceNumber || ''}
              paymentError={!!invoice?.dunningStatus?.length}
              pdfLink={pdfLink}
              transactionAccountId={transactionAccount?.[0]?.id}
              grandTotal={grandTotal}
              paymentMethod={invoice.paymentMethod as InvoicePaymentMethod}
            />
          </td>
        );
      case 'Sold-to address':
        return <td>{getAddress(shipTo?.address, shipTo?.fullName as string | undefined)}</td>;
      case 'Billing address':
        return <td>{getAddress(billTo?.address)}</td>;
      case 'Due on':
        return (
          <td>
            {dueDate ? (
              <div
                className={classNames({
                  'overdue-date': InvoiceUtil.isDueDateOverdue(status ?? '', !!invoice?.dunningStatus?.length, dueDate),
                })}
              >
                <FormattedDate date={dueDate} format={NEW_DATE_FORMAT} />
              </div>
            ) : (
              <span className="not-applicable">N/A</span>
            )}
          </td>
        );
      case 'Issued on':
        return (
          <td>
            {createdTimestamp ? (
              <FormattedDate date={createdTimestamp} showTime={true} format={NEW_DATE_TIME_FORMAT} />
            ) : (
              <span className="not-applicable">N/A</span>
            )}
          </td>
        );
      case 'Payment method':
        return (
          <SimpleTD
            text={getPaymentMethod(paymentMethod, creditCard)}
            className={classNames({
              'not-applicable': getIsFreeInvoice(paymentMethod) || !paymentMethod,
            })}
          />
        );
      case 'Amount':
        return (
          <td className="invoice-amount-cell">
            {!getIsFreeInvoice(paymentMethod) && total ? (
              <formattedV2.FormattedCurrencyV2 value={total} currency={paymentCurrency} showCurrencyCode />
            ) : (
              'Free'
            )}
          </td>
        );
    }
  });
  return rowData;
};

export const CloudInvoiceTab = memo(
  ({ cloudId, isCloudSiteInvoiceTab = true }: { cloudId: string; isCloudSiteInvoiceTab?: boolean }) => {
    const [rowsPerPage, setRowsPerPage] = useState<number>(DEFAULT_ROWS_PER_PAGE);
    const [currentPage, setCurrentPage] = useState<number>(DEFAULT_PAGE);
    const [columnsList, setColumnList] = useState<Array<ColumnList>>(getHead());
    const { isSidebarOpen, payload } = useSidebar();

    const { loading, error, data, refetch } = useQuery<
      generatedTypesNew.InvoiceSearchQuery,
      generatedTypesNew.QueryInvoiceSearchArgs
    >(fetchInvoicesForCloud, {
      variables: {
        searchOptions: {
          ...{
            size: rowsPerPage,
            from: 0,
          },
          cloudSiteId: cloudId,
          showCCPData: true,
        },
      },
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
    });

    //variables to use for paging and populating our template
    const totalCount = data?.invoiceSearch?.totalHits ?? 0;
    const hasNoResults = totalCount === 0;
    const nodes = data?.invoiceSearch?.results;

    const onPageChange = useCallback(
      (newPage: number) => {
        setCurrentPage(newPage);
        refetch({
          searchOptions: {
            ...{
              size: rowsPerPage,
              from: (newPage - 1) * rowsPerPage,
            },
            cloudSiteId: cloudId,
          },
        });
      },
      [rowsPerPage, currentPage, refetch]
    );

    const handleRowChange = useCallback(
      (newRowPerPage: number) => {
        setRowsPerPage(newRowPerPage);
        setCurrentPage(1);
      },
      [rowsPerPage]
    );

    const getSelectedColumns = useCallback(
      (columnsList: ColumnList[]) => {
        let columns = [];
        return (columns = columnsList.filter(
          (column) => column.isChecked || !Object.prototype.hasOwnProperty.call(column, 'isChecked')
        ));
      },
      [columnsList]
    );

    const props = {
      children: (
        <>
          <ColGroup columns={getSelectedColumns(columnsList)} />
          <TableHead columns={getSelectedColumns(columnsList)} />
          <TableBody>
            {(nodes ?? []).map((invoice: any, index: number) => {
              const { id, invoiceNumber, sourceSystem, refunds } = invoice;

              const identifier = getIdentifier({
                id,
                invoiceNumber,
                sourceSystem,
              });
              const rowPayload = {
                invoiceId: identifier,
              };
              return (
                <>
                  <OpenSidebarRow
                    key={`invoice-table-row-${index}`}
                    sidebar={GloballyAllowedSidebar.INVOICE_PREVIEW}
                    payload={rowPayload}
                    sidebarOptions={{
                      isOverlay: true,
                      sidebarWidth: '968px',
                    }}
                    isSelected={false}
                    data-testid={`invoice-table-row-${index}`}
                    className={classNames({
                      'grey-row': index % 2 !== 0,
                      'white-row': index % 2 === 0,
                    })}
                    hasDefaultClassName={false}
                  >
                    {getColumnData(getSelectedColumns(columnsList), invoice).map((node) => node)}
                  </OpenSidebarRow>
                  <>
                    {!isEmpty(refunds) &&
                      //@ts-ignore
                      (refunds ?? []).map((ccpCreditNote: Refunds) => {
                        const refundPayload = {
                          refundId: ccpCreditNote?.creditNoteNumber || ccpCreditNote?.id,
                        };
                        const isChildRowSelected = isSidebarOpen && isEqual(refundPayload, payload);
                        return (
                          <InvoiceChildRefundRow
                            key={ccpCreditNote.id}
                            parentColumns={6}
                            hideActions={false}
                            refund={ccpCreditNote}
                            isSelected={isChildRowSelected}
                            isCloudSiteInvoiceTab={isCloudSiteInvoiceTab}
                          />
                        );
                      })}
                  </>
                </>
              );
            })}
          </TableBody>
        </>
      ),
      noResults: hasNoResults,
      isLoading: loading,
      error: error?.toString(),
      heading: 'Invoices',
      selectableResults: true,
      isPaginationEnabled: totalCount > DEFAULT_ROWS_PER_PAGE,
      totalCount: totalCount,
      rowsPerPage: rowsPerPage,
      currentPage: currentPage,
      onPageChange: onPageChange,
      onRowChange: handleRowChange,
      testId: 'cloud-invoice-tab',
      emptyStateProps: {
        header: '',
        description: 'No invoices found',
      },
    };

    return (
      <div data-testid="cloud-invoice-tab">
        <CustomDynamicResultTable {...props}></CustomDynamicResultTable>
      </div>
    );
  }
);
